import { useCallback, useEffect, useRef, useState } from 'react'
import type { Task } from '../../../../../lib/db/models'
import { useDataSource } from '../../../../../lib/dataSource'
import { isQuickNote } from '../../../../../lib/db/taskFilters'

/**
 * **Aufgaben festhalten, die Kurz notiert verlassen** (Abnahme F-KN3).
 *
 * `isQuickNote` verlangt `startDate == null` (`lib/db/taskFilters.ts`): Sobald
 * eine Notiz ein Startdatum bekommt, fällt sie aus der Abfrage und die Zeile
 * verschwindet. Ohne ein Wort sieht das aus, als wäre sie weg.
 *
 * **Erkannt wird am Schreibweg, nicht am Listenvergleich.** Die Abfrage wirft
 * die Aufgabe heraus, bevor man sie fragen könnte, warum sie geht — ein „wer
 * fehlt jetzt?" zählte Abgehaktes und Gelöschtes mit. Hier steht stattdessen die
 * Absicht: ein `startDate`, das von leer auf einen Wert geht.
 *
 * **Eine Wiederholung ist mitgemeint.** Die Regel setzt das Datum, sie läuft
 * also durch dieselbe Tür.
 *
 * Ruhen, Löschen und Abhaken nehmen die Zeile ebenfalls aus der Liste — die
 * melden sich aber alle schon selbst (Toast, Rückgängig, Geschafft-Schalter)
 * und gehören hier bewusst nicht her.
 *
 * **Die zweite Tür: Herauslösen** (gemeldet 28.09.2026). Eine Unteraufgabe mit
 * eigenem Wann (oder Ruhe) wird dabei eine Aufgabe, die nie hier stand — sie ging
 * sofort nach Demnächst und schien verschwunden. Die Seite meldet sie an
 * (`noteUnnested`); gehalten wird, sobald sie als Aufgabe dasteht und nicht nach
 * Kurz notiert gehört. Beobachtet, nicht gelesen: Beim Herausziehen hört die Seite
 * davon, bevor geschrieben ist.
 */
export function useInboxDepartures(tasks: Task[] | undefined, isActive: boolean): {
  /** Die gehaltenen Aufgaben, in der Reihenfolge ihres Abgangs. */
  held: Task[]
  /**
   * Meldet eine Änderung an; hält fest, was dadurch geht, und sagt wie viele
   * das waren. Die Zahl braucht der Aufrufer, um die Auswahl aufzuheben: Was
   * die Liste verlässt, kann nicht weiter ausgewählt sein.
   */
  noteUpdate: (taskIds: readonly string[], updates: Partial<Task>) => number
  /** Herausgelöste Unteraufgaben — die, die nicht nach Kurz notiert gehören, werden gehalten. */
  noteUnnested: (taskIds: readonly string[]) => void
  /** „OK" — die gehaltenen Zeilen dürfen gehen. */
  confirm: () => void
  /** Eine Marke an der Quittungs-Zeile: schreiben und die gehaltene Kopie mitziehen. */
  updateHeld: (taskId: string, changes: Partial<Omit<Task, 'id'>>) => void
} {
  const [held, setHeld] = useState<Task[]>([])
  const tasksRef = useRef(tasks)
  tasksRef.current = tasks

  const [watching, setWatching] = useState<string[]>([])
  const source = useDataSource()
  const watched = source.useTasksByIds(watching)
  useEffect(() => {
    if (!watched || watching.length === 0) return
    const landed = watching.map(id => watched.get(id)).filter((task): task is Task => !!task && task.parentTaskId == null)
    if (landed.length === 0) return
    const gone = landed.filter(task => !task.deletedAt && !isQuickNote(task))
    if (gone.length > 0) {
      setHeld(prev => {
        const neu = gone.filter(task => !prev.some(gehalten => gehalten.id === task.id))
        return neu.length === 0 ? prev : [...prev, ...neu]
      })
    }
    setWatching(prev => prev.filter(id => !landed.some(task => task.id === id)))
  }, [watched, watching])

  const noteUnnested = useCallback((taskIds: readonly string[]) => {
    setWatching(prev => [...new Set([...prev, ...taskIds])])
  }, [])

  const noteUpdate = useCallback((taskIds: readonly string[], updates: Partial<Task>): number => {
    if (updates.startDate == null) return 0
    const vorhanden = tasksRef.current
    if (!vorhanden) return 0

    const abgang = taskIds
      .map(id => vorhanden.find(task => task.id === id))
      // Wer schon ein Datum trug, stand hier nie — der geht auch nicht ab.
      .filter((task): task is Task => !!task && task.startDate == null)
      .map(task => ({ ...task, startDate: updates.startDate ?? null }))

    if (abgang.length === 0) return 0
    setHeld(prev => {
      const neu = abgang.filter(task => !prev.some(gehalten => gehalten.id === task.id))
      return neu.length === 0 ? prev : [...prev, ...neu]
    })
    return abgang.length
  }, [])

  const confirm = useCallback(() => setHeld([]), [])

  /**
   * Eine Marke an der Quittungs-Zeile geändert: schreiben und die gehaltene Kopie mitziehen — die Zeile zeigt
   * die Kopie, und die liest sonst niemand nach (Code-Review #313).
   */
  const updateHeld = useCallback((taskId: string, changes: Partial<Omit<Task, 'id'>>) => {
    setHeld(prev => prev.map(task => (task.id === taskId ? { ...task, ...changes } : task)))
    void source.updateTask(taskId, changes)
  }, [source])

  // Beim Verlassen der Seite ist aufgeräumt — die Meldung gilt dem Blick, der
  // gerade auf der Liste liegt.
  useEffect(() => {
    if (isActive) return
    setHeld(prev => (prev.length === 0 ? prev : []))
    setWatching(prev => (prev.length === 0 ? prev : []))
  }, [isActive])

  return { held, noteUpdate, noteUnnested, confirm, updateHeld }
}
